import { useQuery } from "@tanstack/react-query";
import {
  fetchCampaigns,
  fetchCampaignDetails,
  fetchCampaignMetrics,
  fetchProblematicCampaigns,
  fetchCampaignActions,
  fetchCampaignActionDetails,
  type FetchCampaignsParams,
} from "../api/campaignApi";
import type { CampaignDailyMetric, CampaignDetails } from "./types";

export const useCampaignsQuery = ({
  clientLogin,
  search = "",
  page = 1,
  limit = 20,
}: FetchCampaignsParams = {}) => {
  return useQuery({
    queryKey: ["campaigns", { clientLogin, search, page, limit }],
    queryFn: () => fetchCampaigns({ clientLogin, search, page, limit }),
    staleTime: 60 * 1000,
  });
};

export const useCampaignDetailsQuery = (id: string | number | undefined) => {
  return useQuery<CampaignDetails>({
    queryKey: ["campaign", id],
    queryFn: () => fetchCampaignDetails(id!),
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });
};

export const useCampaignMetricsQuery = (
  id: string | number | undefined,
  days = 7
) => {
  return useQuery<CampaignDailyMetric[]>({
    queryKey: ["campaign-metrics", id, days],
    queryFn: () => fetchCampaignMetrics(id!, days),
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });
};

export const useProblematicCampaignsQuery = (clientLogin?: string) => {
  return useQuery({
    queryKey: ["campaigns-problems", clientLogin],
    queryFn: () => fetchProblematicCampaigns(clientLogin),
    staleTime: 60 * 1000,
  });
};

export const useCampaignActionsQuery = (
  id: string | number | undefined,
  params: { page?: number; limit?: number } = {}
) => {
  return useQuery({
    queryKey: ["campaign-actions", id, params],
    queryFn: () => fetchCampaignActions(id!, params),
    enabled: Boolean(id),
    staleTime: 30 * 1000,
  });
};

export const useCampaignActionDetailsQuery = (
  campaignId: string | number | undefined,
  actionId: string | null | undefined
) => {
  return useQuery({
    queryKey: ["campaign-action-details", campaignId, actionId],
    queryFn: () => fetchCampaignActionDetails(campaignId!, actionId!),
    enabled: Boolean(campaignId && actionId),
    staleTime: 60 * 1000,
  });
};

